import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import {
  toWidget,
  viewToModelPositionOutsideModelElement,
} from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import PlaceholderCommand from './placeholder-command';
import LibraryItemStore from '../library-item-store';

export default class PlaceholderEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();

    //register the PlaceholderCommand with the editor
    this.editor.commands.add(
      'placeholder',
      new PlaceholderCommand(this.editor)
    );

    //
    this.editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(this.editor.model, (viewElement) =>
        viewElement.hasClass('placeholder')
      )
    );

    this.editor.config.define('placeholderConfig', {
      types: [
        /* types defined in app.js */
      ],
    });
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register('placeholder', {
      // Allow wherever text is allowed:
      allowWhere: '$text',

      // The placeholder will act as an inline node:
      isInline: true,

      // The inline widget is self-contained so it cannot be split by the caret and can be selected:
      isObject: true,

      // The placeholder can have many types, like date, name, surname, etc...
      allowAttributes: ['id'],
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: ['placeholder'],
      },
      model: (viewElement, { writer }) => {
        // Extract the library item id from the 'id' attribute.
        const value = viewElement.getAttribute('id');
        return writer.createElement('placeholder', { id: value });
      },
    });

    //editing downcast converter
    conversion.for('editingDowncast').elementToElement({
      model: 'placeholder',
      view: (modelItem, { writer }) => {
        const widgetElement = createEditingPlaceholderView(modelItem, writer);

        // Enable widget handling on a placeholder element inside the editing view.
        return toWidget(widgetElement, writer);
      },
    });

    //datadowncast converter
    conversion.for('dataDowncast').elementToElement({
      model: 'placeholder',
      view: (modelItem, { writer }) => {
        const widgetElement = createDataPlaceholderView(modelItem, writer);

        // Enable widget handling on a placeholder element inside the editing view.
        return toWidget(widgetElement, writer);
      },
    });

    // Helper method for the editing downcast converter.
    function createEditingPlaceholderView(modelItem, viewWriter) {
      const id = modelItem.getAttribute('id');
      const data = LibraryItemStore.get(id);
      const className = data ? 'placeholder' : 'no-content';
      const placeholderView = viewWriter.createContainerElement('div', {
        class: className,
        id,
      });

      const uiElement1 = viewWriter.createUIElement(
        'div',
        null,
        function (domDocument) {
          const domElement1 = this.toDomElement(domDocument);

          if (data) {
            domElement1.innerHTML = data.rendered;
          }

          return domElement1;
        }
      );

      viewWriter.insert(
        viewWriter.createPositionAt(placeholderView, 0),
        uiElement1
      );

      return placeholderView;
    }

    // Helper method for the data downcast converter.
    function createDataPlaceholderView(modelItem, viewWriter) {
      const placeholderView = viewWriter.createContainerElement('span', {
        class: 'placeholder',
        id: modelItem.getAttribute('id'),
      });

      return placeholderView;
    }
  }
}
